import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { EssentialsGapList } from '@/components/instructor/EssentialsGapList'
import { formatPrecise } from '@/lib/format'
import type { MissingEssential } from '@/hooks/useInstructorThemeStats'

interface EssentialsBrowserProps {
  essentials: MissingEssential[]
}

// Rank <= this is "the well-known canon"; past it is genuine deep-cut
// territory rather than top-40-style essentials. Matches the list's old
// hard cap, so "Essentials" reproduces exactly what used to be the whole
// list, with "Deep cuts" as the new, larger long tail beyond it.
const ESSENTIALS_THRESHOLD = 100
const PAGE_SIZE = 24

type Tier = 'all' | 'essentials' | 'deep_cuts'
type Familiarity = 'all' | 'new' | 'known'

export function EssentialsBrowser({ essentials }: EssentialsBrowserProps) {
  const [search, setSearch] = useState('')
  const [tier, setTier] = useState<Tier>('all')
  const [familiarity, setFamiliarity] = useState<Familiarity>('all')
  const [page, setPage] = useState(1)

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()

    return essentials.filter((track) => {
      if (tier === 'essentials' && track.rank > ESSENTIALS_THRESHOLD) return false
      if (tier === 'deep_cuts' && track.rank <= ESSENTIALS_THRESHOLD) return false
      if (familiarity === 'new' && track.artist_already_played) return false
      if (familiarity === 'known' && !track.artist_already_played) return false
      if (query && !track.track_name.toLowerCase().includes(query) && !track.artist_name.toLowerCase().includes(query)) {
        return false
      }
      return true
    })
  }, [essentials, tier, familiarity, search])

  // Any filter/search change can shrink the result set below the current
  // page -- always land back on page 1 rather than showing an empty page.
  useEffect(() => {
    setPage(1)
  }, [tier, familiarity, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search artist or track..."
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Tabs value={tier} onValueChange={(v) => setTier(v as Tier)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="essentials">Essentials</TabsTrigger>
              <TabsTrigger value="deep_cuts">Deep cuts</TabsTrigger>
            </TabsList>
          </Tabs>

          <Tabs value={familiarity} onValueChange={(v) => setFamiliarity(v as Familiarity)}>
            <TabsList>
              <TabsTrigger value="all">Any artist</TabsTrigger>
              <TabsTrigger value="new">New artists</TabsTrigger>
              <TabsTrigger value="known">Knows artist</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {filtered.length > 0 ? (
        <>
          <EssentialsGapList essentials={pageItems} />

          <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
            <span className="text-xs text-muted-foreground">
              {formatPrecise(filtered.length)} {filtered.length === 1 ? 'track' : 'tracks'}
              {filtered.length !== essentials.length ? ` (of ${formatPrecise(essentials.length)})` : ''}
            </span>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="text-xs tabular-nums text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No tracks match your filters.</p>
      )}
    </div>
  )
}
